import React, { useState, useEffect } from "react";
import { 
  Users, 
  Search, 
  Trash2, 
  Activity,
  UserRound,
  FileText,
  Phone,
  Calendar
} from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import { db } from "../../firebase/config";
import { useToast } from "../../context/ToastContext";
import { ref, onValue, remove } from "firebase/database";
import "./admin.css";

const ManagePatients = () => {
  const { showAlert } = useToast();
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Synchronize Patient Registry
    const usersRef = ref(db, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data)
          .filter(([uid, val]) => val.role === "patient")
          .map(([uid, val]) => ({ uid, ...val }));
        setPatients(list);
      } else {
        setPatients([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleDeletePatient = async (uid) => {
    if (window.confirm("CONFIRM_ACTION: Permanently purge this patient's clinical node?")) {
      try {
        await remove(ref(db, `users/${uid}`));
        showAlert("IDENTITY_PURGED: Patient record successfully removed from registry.", "success");
      } catch (error) {
        showAlert("PROTOCOL_ERROR: " + error.message, "error");
      }
    }
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.customId && p.customId.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="admin-shell">
      <AdminSidebar />
      
      <main className="admin-content">
        <header className="admin-header">
          <div className="header-info">
            <h1>PATIENT_REGISTRY</h1>
            <p>OVERSIGHT_NODE: CLINICAL_CENSUS_v3.1</p>
          </div>
          <div className="system-status">
            <UserRound size={14} className="icon-blue pulse" strokeWidth={3} />
            <span>SESSION: AUTHENTICATED</span>
          </div>
        </header>

        <section className="registry-controls glass-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
          <div className="search-group" style={{ maxWidth: 'unset' }}>
            <Search size={18} className="icon-blue" strokeWidth={2.5} />
            <input 
              type="text" 
              placeholder="FILTER_BY: NAME, EMAIL, OR_PATIENT_ID..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', letterSpacing: '1px' }}
            />
          </div>
          <div className="registry-summary" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--admin-accent-blue)' }}>
            REGISTERED_NODES: [ {patients.length.toString().padStart(3, '0')} ]
          </div>
        </section>

        <div className="registry-list-section glass-card">
          <table className="registry-table">
            <thead>
              <tr>
                <th>IDENTITY_NODE</th>
                <th>CONTACT_METADATA</th>
                <th>CENSUS_DATA</th>
                <th>REGISTRATION_DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.length > 0 ? (
                filteredPatients.map((p) => (
                  <tr key={p.uid}>
                    <td>
                      <div className="table-user-info">
                        <div className="user-avatar" style={{ background: 'rgba(59, 130, 246, 0.1)', color: 'var(--admin-accent-blue)' }}>
                          {p.name.charAt(0)}
                        </div>
                        <div className="user-text">
                          <span className="user-name" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem' }}>{p.name}</span>
                          <span className="user-id" style={{ fontSize: '0.7rem', color: 'var(--admin-accent-cyan)', opacity: 0.8 }}>CODE: {p.customId || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-stack" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={12} /> {p.email}</span>
                        <span style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={12} /> {p.phone || "UNSPECIFIED"}</span>
                      </div>
                    </td>
                    <td>
                      <span className="census-badge" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>
                        AGE: {p.age ? p.age.toString().padStart(2, '0') : "00"} | XP: {p.experience || "00"}
                      </span>
                    </td>
                    <td>
                      <div className="date-box" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={14} className="icon-blue" />
                        <span style={{ fontSize: '0.75rem', fontFamily: 'Space Mono, monospace' }}>
                          {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "PENDING"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="icon-btn delete" onClick={() => handleDeletePatient(p.uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-text-dim)' }}>
                          <Trash2 size={16} strokeWidth={2.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty-registry" style={{ padding: '5rem', textAlign: 'center' }}>
                    <Activity size={48} className="icon-dim pulse" />
                    <p style={{ marginTop: '1.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--admin-text-dim)' }}>
                      NO_PATIENT_NODES_FOUND_MATCHING_SEARCH_PARAM
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default ManagePatients;
